import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  ListParams,
  MarketplaceOpportunityAction,
  MarketplaceSignalAction,
  OpportunityDto,
  PaginationMeta,
  RequestMeta,
  SignalDto,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/marketplace`;

function toQuery(params: ListParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createMarketplaceApi(client: ApiClient) {
  return {
    listSignals(params: ListParams = {}) {
      return client.get<{
        signals: SignalDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/signals${toQuery(params)}`);
    },
    getSignal(id: string) {
      return client.get<{ signal: SignalDto; meta: RequestMeta }>(
        `${BASE}/signals/${id}`,
      );
    },
    createSignal(body: Record<string, unknown>) {
      return client.post<{ signal: SignalDto; meta: RequestMeta }>(
        `${BASE}/signals`,
        body,
        newIdempotencyKey(),
      );
    },
    applySignalAction(
      id: string,
      action: MarketplaceSignalAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/signals/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listOpportunities(params: ListParams = {}) {
      return client.get<{
        opportunities: OpportunityDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/opportunities${toQuery(params)}`);
    },
    getOpportunity(id: string) {
      return client.get<{ opportunity: OpportunityDto; meta: RequestMeta }>(
        `${BASE}/opportunities/${id}`,
      );
    },
    createOpportunity(body: Record<string, unknown>) {
      return client.post<{ opportunity: OpportunityDto; meta: RequestMeta }>(
        `${BASE}/opportunities`,
        body,
        newIdempotencyKey(),
      );
    },
    patchOpportunity(id: string, body: Record<string, unknown>) {
      return client.patch<{ opportunity: OpportunityDto; meta: RequestMeta }>(
        `${BASE}/opportunities/${id}`,
        body,
        newIdempotencyKey(),
      );
    },
    applyOpportunityAction(
      id: string,
      action: MarketplaceOpportunityAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/opportunities/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
  };
}

export type MarketplaceApi = ReturnType<typeof createMarketplaceApi>;
